import { useDispatch, useSelector } from 'react-redux';
import { Button, InlineLink, Txt } from '@vst/beam';
import { useHistory } from 'react-router-dom';

import { ROUTES } from '@app/router/constants';
import { selectAddOns, setErrorDisclosureCheck, setScrollToConsent } from '@app/redux/addOns';
import { Pages, selectStreamOnPromo, setCurrentPage, submitFullOrder } from '@app/redux/streamOnPromo';

import { AddressInput } from '@mfe/shared/schema-types';

import { LoadingAnimatedButton } from '../LoadingAnimatedButton';
import CartPng from './Cart.png';
import { useTranslation } from '../../utils';

import styles from './Cart.module.scss';

function isDefined<T>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null;
}

export const priceDecimalFormat = (price: number | string, country: string): string => {
  let currency;
  let locale = '';
  const aton = (val?: string | null): number => (isNaN(Number(val)) ? 0 : Number(val));

  if (typeof price === 'string') {
    price = aton(price);
  }

  switch (country) {
    case 'Brazil':
      currency = 'BRL';
      locale = 'en-BR';
      break;
    default:
      currency = 'USD';
      locale = 'en-US';
  }
  const roundedPrice = Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  });
  const wholePrice = Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  });

  if (price % 1 !== 0) {
    return wholePrice.format(price).replace(/\s/g, '');
  } else {
    return roundedPrice.format(price).replace(/\s/g, '');
  }
};

type CartProps = {
  setShowTermsModal: (value: boolean) => void;
};

export const Cart = ({ setShowTermsModal }: CartProps) => {
  const { t: streamonTranslate } = useTranslation('StreamOn');
  const { t: placeOrderTranslate } = useTranslation('PlaceOrder');
  const { t: purchaseFlow } = useTranslation('PurchaseFlow');

  const dispatch = useDispatch();
  const history = useHistory();

  const {
    shippingAddress,
    isEditingNumber,
    userDetails,
    isEditingAddress,
    offerDetails,
    isOrderSubmitting,
    configureStreamLoading,
  } = useSelector(selectStreamOnPromo);

  const { shouldDisplaySms, eicRequired } = offerDetails;

  const { eicChecked, eicMessageLoading } = useSelector(selectAddOns);

  const disableCompleteOrder = !(
    shippingAddress.addressLines[0] &&
    shippingAddress.municipality &&
    shippingAddress.region &&
    shippingAddress.postalCode &&
    !isEditingAddress &&
    !isEditingNumber &&
    shouldDisplaySms !== null &&
    (!eicRequired || (!configureStreamLoading && !eicMessageLoading))
  );

  const onNext = () => {
    if (eicRequired && !eicChecked) {
      dispatch(setErrorDisclosureCheck(true));
      dispatch(setScrollToConsent(true));
      return;
    }
    dispatch(
      submitFullOrder({
        address: shippingAddress as AddressInput,
        isEditingNumber: isEditingNumber,
        phoneNumber: userDetails?.phoneNumber,
      })
    );
    window.scrollTo(0, 0);
  };

  const onBack = () => {
    history.push(ROUTES.HOME);
    dispatch(setCurrentPage(Pages.login));
  };

  const onTermsClick = (event: React.MouseEvent<HTMLElement>) => {
    if (event) event.preventDefault();
    setShowTermsModal(true);
  };

  const country = 'US';
  const { fullPrice, discountValue, loading } = offerDetails;

  const fullPriceNumber = fullPrice ? Number(fullPrice.slice(0, fullPrice.length).replace(/\/mo/g, '')) : 0;
  const fullPriceValue = Number(fullPriceNumber) ? fullPriceNumber : 0;

  const discount = isDefined(fullPriceValue) ? fullPriceValue + discountValue : discountValue;
  const monthlyFee = isDefined(fullPriceValue) ? priceDecimalFormat(fullPriceValue, country) : 0.0;
  const displayDiscount = priceDecimalFormat(discount, country);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cartSrc: string = (CartPng as any)?.src ?? (CartPng as any);

  return (
    <div data-cy="stream-cart-container" className={styles['containerCard']}>
      <div className={styles['card']}>
        <div>
          <Txt variant="heading5" data-cy="cartTitle">
            {streamonTranslate('Cart.Title')}
          </Txt>
        </div>
        <div className={styles['imageAndText']}>
          <div className={styles['imageBackground']}>
            <img src={cartSrc} alt="cart" />
          </div>
          <Txt variant="bodyLargeRegular" data-cy="cartImageText">
            {streamonTranslate('Cart.Image.Text')}
          </Txt>
        </div>
        <div className={styles['cartPricingContainer']}>
          <div className={styles['cartPricingStyle']} data-cy="monthlyFee">
            <Txt variant="bodyLargeRegular">{streamonTranslate('Cart.ServiceFee')}</Txt>
            {loading ? (
              <div className={styles['loading']} />
            ) : discountValue === 0 ? (
              <div>
                <Txt variant="bodySmallBold" color="success">
                  {streamonTranslate('Cart.Free')}
                </Txt>
              </div>
            ) : (
              <>
                <div className={styles['crossout']}>
                  <Txt variant="bodyLargeRegular" color="inherit">
                    {monthlyFee}
                  </Txt>
                </div>
                <Txt variant="bodyLargeRegular" className={styles['discount']}>
                  {displayDiscount}
                </Txt>
              </>
            )}
          </div>
        </div>
        <div className={styles['termsAndConditions']}>
          <Txt variant="smallRegular" data-cy="termsAndConditions">
            {streamonTranslate('Cart.TermsAndConditions.Text')}
            {
              <InlineLink onClick={onTermsClick} href={''} data-cy="termsAndConditionsLink">
                {streamonTranslate('Cart.TermsAndConditions.TermsText')}.
              </InlineLink>
            }
          </Txt>
        </div>
        <div className={styles['disclosure']}>
          <Txt variant="smallRegular" data-cy="disclosureText">
            {streamonTranslate('Cart.Disclosure')}
          </Txt>
        </div>
        <div className={styles['buttonContainer']}>
          {isOrderSubmitting ? (
            <LoadingAnimatedButton />
          ) : (
            <Button fluid onClick={onNext} disabled={disableCompleteOrder} data-cy="completeOrderButton">
              <Txt variant="labelLarge" color="inherit" data-cy="completeOrderButtonText">
                {placeOrderTranslate('Button')}
              </Txt>
            </Button>
          )}

          <Button fluid disabled={isOrderSubmitting} variant="secondary" onClick={onBack} data-cy="backButton">
            {purchaseFlow('Cancel.Button')}
          </Button>
        </div>
      </div>
    </div>
  );
};
